import { CheckIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import type { InputType } from 'config'
import { Fragment, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { commitTaskImport } from 'services/apis/conditions'
import { Button, Modal, Tooltip } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { taskInputs } from '../constant'
import type { ITaskTemplate } from './types'

interface TaskImportDraft {
  rowIndex: number
  data: Record<string, any>
  missingFields: string[]
  errors: string[]
  selected?: boolean
  existingTaskId?: number
}

interface TaskImportModalProps {
  rows: TaskImportDraft[]
  tasks: ITaskTemplate[]
  assigneeOptions: Record<string, string>
  onClose: () => void
  onComplete: () => void
}

const importInputKeys = [
  'description',
  'assignedTo',
  'assignedToSecondary',
  'assignedToManager',
  'dueDays',
  'replyCcUser',
  'notes',
]

const TASK_DESC_EXISTS_MSG =
  'Task Description already exists. Update your excel sheet and reimport, or click the edit icon on the task line to change the description or number.'
const TASK_NO_UNKNOWN_MSG = 'No does not exist. Leave No blank to add a new task.'

const STALE_ERRORS = [
  'No must be a number.',
  'No is duplicated in the import file.',
  'Description is duplicated in the import file.',
  TASK_DESC_EXISTS_MSG,
  TASK_NO_UNKNOWN_MSG,
]

const selectedAssignees = (value: any): string[] => {
  if (Array.isArray(value)) return value.map((item) => `${item}`.trim()).filter(Boolean)
  if (value && typeof value === 'object') return Object.keys(value).filter((key) => value[key])
  return []
}

const readNo = (value: any) => {
  const text = `${value ?? ''}`.trim()
  if (!text) return { hasNo: false, no: 0, invalid: false }
  const no = Number(text)
  const invalid = !Number.isInteger(no) || no <= 0
  return { hasNo: !invalid, no, invalid }
}

const requiredFields = [
  { key: 'description', title: 'Description' },
  { key: 'assignedTo', title: 'Assigned to' },
  { key: 'dueDays', title: 'Due days' },
  { key: 'notes', title: 'Notes' },
]

const assigneeFieldTitles: Record<string, string> = {
  assignedTo: 'Assigned to',
  assignedToSecondary: 'Assigned to (Secondary)',
  assignedToManager: 'Assigned to Manager',
  replyCcUser: 'Reply CC User',
}

const matchInfo = (draft: TaskImportDraft, tasks: ITaskTemplate[]) => {
  const { hasNo, no, invalid } = readNo(draft.data.no)
  const description = `${draft.data.description || ''}`.trim().toLowerCase()
  const matchByNo = hasNo ? tasks.find((task) => Number(task.no) === no) : undefined
  const matchByDescription = description
    ? tasks.find((task) => `${task.description || ''}`.trim().toLowerCase() === description)
    : undefined
  const descriptionTakenByOther = !!(matchByDescription && (!matchByNo || matchByDescription.id !== matchByNo.id))
  const isUpdate = !!matchByNo && !descriptionTakenByOther
  return {
    hasNo,
    no,
    invalid,
    description,
    matchByNo,
    matchByDescription,
    descriptionTakenByOther,
    isUpdate,
    existingTaskId: isUpdate ? matchByNo!.id : undefined,
  }
}

const draftErrors = (
  draft: TaskImportDraft,
  index: number,
  allDrafts: TaskImportDraft[],
  tasks: ITaskTemplate[],
  assigneeOptions: Record<string, string>,
) => {
  const errors = new Set<string>((draft.errors || []).filter((error) => !STALE_ERRORS.includes(error)))
  const addError = (message: string) => errors.add(message)

  requiredFields.forEach((field) => {
    const value = draft.data[field.key]
    const empty = Array.isArray(value) ? value.length === 0 : `${value ?? ''}`.trim() === ''
    if (empty) addError(`${field.title} is required.`)
  })

  const { hasNo, no, invalid, description, matchByNo, descriptionTakenByOther, isUpdate } = matchInfo(draft, tasks)
  if (invalid) addError('No must be a number.')
  if (!isUpdate) {
    if (descriptionTakenByOther) addError(TASK_DESC_EXISTS_MSG)
    if (hasNo && !matchByNo) addError(TASK_NO_UNKNOWN_MSG)
  }

  if (
    hasNo &&
    allDrafts.some((item, itemIndex) => {
      const itemNo = readNo(item.data.no)
      return itemIndex !== index && itemNo.hasNo && itemNo.no === no
    })
  ) {
    addError('No is duplicated in the import file.')
  }
  if (
    description &&
    allDrafts.some(
      (item, itemIndex) =>
        itemIndex !== index && `${item.data.description || ''}`.trim().toLowerCase() === description,
    )
  ) {
    addError('Description is duplicated in the import file.')
  }

  Object.entries(assigneeFieldTitles).forEach(([key, title]) => {
    const invalidValues = selectedAssignees(draft.data[key]).filter((item) => !assigneeOptions[item])
    if (invalidValues.length) addError(`${title} contains invalid values: ${invalidValues.join(', ')}.`)
  })

  return Array.from(errors)
}

export function TaskImportModal({ rows, tasks, assigneeOptions, onClose, onComplete }: TaskImportModalProps) {
  const [drafts, setDrafts] = useState<TaskImportDraft[]>(() => {
    const next = rows.map((row) => ({ ...row, selected: false }))
    return next.map((draft, index) => ({
      ...draft,
      selected: draftErrors(draft, index, next, tasks, assigneeOptions).length === 0,
    }))
  })
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(false)

  const inputs = useMemo(() => {
    const baseInputs = taskInputs()
    importInputKeys.forEach((key) => {
      if (baseInputs[key]?.inputType === 'multiselect') baseInputs[key].options = assigneeOptions
    })
    return baseInputs
  }, [assigneeOptions])

  const setDraft = (index: number, draft: TaskImportDraft) => {
    setDrafts((prev) => prev.map((item, itemIndex) => (itemIndex === index ? draft : item)))
  }

  const updateField = (index: number, key: string, value: any) => {
    const draft = { ...drafts[index], data: { ...drafts[index].data, [key]: value }, errors: [] }
    setDraft(index, draft)
  }

  const toggleExpandedRow = (rowIndex: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }

  const getDraftErrors = (draft: TaskImportDraft, index: number) =>
    draftErrors(draft, index, drafts, tasks, assigneeOptions)

  const isDraftValid = (draft: TaskImportDraft, index: number) => getDraftErrors(draft, index).length === 0

  const commitDrafts = async (targetDrafts: Array<{ draft: TaskImportDraft; index: number }>) => {
    if (!targetDrafts.length) return

    setIsLoading(true)
    try {
      const res = await commitTaskImport(
        targetDrafts.map(({ draft }) => {
          const { existingTaskId } = matchInfo(draft, tasks)
          return {
            rowIndex: draft.rowIndex,
            data: {
              ...draft.data,
              assignedTo: selectedAssignees(draft.data.assignedTo),
              assignedToSecondary: selectedAssignees(draft.data.assignedToSecondary),
              assignedToManager: selectedAssignees(draft.data.assignedToManager),
              replyCcUser: selectedAssignees(draft.data.replyCcUser),
            },
            existingTaskId,
          }
        }),
      )

      const failedRows = (res.results || []).filter((result: any) => !result.success)
      const successfulRows = new Set(
        (res.results || []).filter((result: any) => result.success).map((result: any) => result.rowIndex),
      )
      setDrafts((prev) =>
        prev
          .filter((draft) => !successfulRows.has(draft.rowIndex))
          .map((draft) => {
            const failedRow = failedRows.find((result: any) => result.rowIndex === draft.rowIndex)
            return failedRow ? { ...draft, errors: [failedRow.message] } : draft
          }),
      )

      toast(res.message || 'Task import completed.', { type: failedRows.length ? 'warning' : 'success' })
      onComplete()
      if (!failedRows.length) onClose()
    } finally {
      setIsLoading(false)
    }
  }

  const validSelected = drafts
    .map((draft, index) => ({ draft, index }))
    .filter(({ draft, index }) => draft.selected && isDraftValid(draft, index))

  return (
    <Modal isOpen title="Import Tasks" titleOkay="" titleCancel="Close" onClose={onClose} loading={isLoading}>
      <div className="w-[1200px] max-w-full">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-600">
            Complete missing fields, then import each task or all valid selected tasks. Matching No + Description
            updates the existing task. Leave No blank to add a new task.
          </p>
          <Button disabled={!validSelected.length || isLoading} onClick={() => commitDrafts(validSelected)}>
            Import All Valid
          </Button>
        </div>
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs uppercase bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-2">Use</th>
                <th className="px-2 py-2">Row</th>
                <th className="px-2 py-2">No</th>
                <th className="px-2 py-2">Description</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft, index) => {
                const errors = getDraftErrors(draft, index)
                const isValid = errors.length === 0
                const isExpanded = expandedRows.has(draft.rowIndex)
                const { isUpdate } = matchInfo(draft, tasks)
                return (
                  <Fragment key={draft.rowIndex}>
                    <tr className={`border-b align-top ${isValid ? 'bg-white' : 'bg-red-50'}`}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={!!draft.selected}
                          onChange={(event) => setDraft(index, { ...draft, selected: event.target.checked })}
                        />
                      </td>
                      <td className="px-2 py-2">{draft.rowIndex}</td>
                      <td className="px-2 py-2">{draft.data.no || ''}</td>
                      <td className="px-2 py-2">{draft.data.description}</td>
                      <td className="px-2 py-2">
                        {isValid ? (
                          <span className={isUpdate ? 'text-amber-700' : 'text-green-700'}>
                            {isUpdate ? 'Ready (update)' : 'Ready'}
                          </span>
                        ) : (
                          <ul className="text-red-700 list-disc pl-4">
                            {errors.map((error, errorIndex) => (
                              <li key={errorIndex}>{error}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-start gap-2">
                          <Tooltip message={isExpanded ? 'Close Edit' : 'Edit'} placement="right">
                            <button
                              aria-label={isExpanded ? 'Close edit' : 'Edit task'}
                              className="btn-icon mt-0.5"
                              type="button"
                              onClick={() => toggleExpandedRow(draft.rowIndex)}
                            >
                              <PencilSquareIcon className="w-4 h-4 text-shade-blue" />
                            </button>
                          </Tooltip>
                          <Tooltip message={isUpdate ? 'Update Task' : 'Import Task'} placement="right">
                            <button
                              aria-label={isUpdate ? 'Update task' : 'Import task'}
                              className={`btn-icon ${!isValid || isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                              disabled={!isValid || isLoading}
                              type="button"
                              onClick={() => commitDrafts([{ draft, index }])}
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={`border-b ${isValid ? 'bg-white' : 'bg-red-50'}`}>
                        <td className="px-2 py-3" colSpan={6}>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="input">
                              <RenderInput
                                input={
                                  {
                                    inputType: 'text',
                                    title: 'No',
                                    value: draft.data.no ?? '',
                                    error:
                                      errors.find(
                                        (error) =>
                                          error === 'No must be a number.' ||
                                          error === 'No is duplicated in the import file.' ||
                                          error === TASK_NO_UNKNOWN_MSG,
                                      ) || '',
                                  } as InputType
                                }
                                Key="no"
                                onChange={(_key: string, value: any) => updateField(index, 'no', value)}
                              />
                            </div>
                            {importInputKeys.map((key) => {
                              const input = {
                                ...inputs[key],
                                value: draft.data[key],
                                error:
                                  errors.find(
                                    (error) =>
                                      error.startsWith(`${inputs[key].title} is required`) ||
                                      error.startsWith(`${inputs[key].title} contains`) ||
                                      (key === 'description' &&
                                        (error === TASK_DESC_EXISTS_MSG ||
                                          error === 'Description is duplicated in the import file.')),
                                  ) || '',
                              } as InputType
                              return (
                                <div className="input" key={key}>
                                  <RenderInput
                                    input={input}
                                    Key={key}
                                    onChange={(_key: string, value: any) => updateField(index, key, value)}
                                  />
                                </div>
                              )
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
